import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesRight, faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { fetchThemeData, updateMultiple } from "../adminApi";
import { asArray, isOn, notifyError, notifySuccess, toHexColor } from "../adminUtils";
import { BackButton, Switch } from "../components";

const MAX_RULES = 6;
const digits = (value) => String(value).replace(/[^0-9]/g, "");

function ColorRow({ label, value, onChange }) {
  return (
    <div className="dc-row">
      <label className="dc-row-label">{label}</label>
      <input type="color" className="dc-color" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function NumberRow({ label, value, onChange, numericOnly, min, max }) {
  return (
    <div className="dc-row">
      <label className="dc-row-label">{label}</label>
      <input
        className="dc-input dc-input-sm"
        type={numericOnly ? "text" : "number"}
        inputMode="numeric"
        min={min}
        max={max}
        step="1"
        value={value}
        onChange={(e) => onChange(numericOnly ? digits(e.target.value) : e.target.value)}
      />
    </div>
  );
}

export default function Rules() {
  const { themeName } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchThemeData(themeName)
      .then((d) => {
        if (cancelled) return;
        const [minutes = "5", seconds = "00"] = String(d.timer || "5:00").split(":");
        const titleColor = toHexColor(d.landing_page_title_color);
        setForm({
          title: d.landing_page_title || "",
          thankYou: d.custom_text_thank_you_page || "",
          rules: asArray(d.rules),
          points: d.points ?? "",
          wrongPoints: d.wrong_points ?? 0,
          count: String(d.category_upload_count ?? ""),
          positive: String(d.positive_upload_count ?? ""),
          negative: String(d.negtive_upload_count ?? ""),
          shuffle: isOn(d.shuffle),
          textColor: titleColor,
          buttonColor: toHexColor(d.button_color),
          cardColor: toHexColor(d.card_color_text),
          iconColor: toHexColor(d.icon_color, titleColor),
          useTimeout: isOn(d.use_timeout),
          minutes,
          seconds,
        });
      })
      .catch((err) => notifyError(err.message));
    return () => {
      cancelled = true;
    };
  }, [themeName]);

  if (!form) return <div className="dc-page">Loading...</div>;

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setRule = (index, value) => set("rules")(form.rules.map((r, i) => (i === index ? value : r)));
  const timerText = `${form.minutes === "" ? 1 : form.minutes}:${form.seconds === "" ? "00" : form.seconds}`;

  const validateMinutes = () => {
    const n = parseInt(form.minutes, 10);
    if (form.minutes !== "" && (isNaN(n) || n < 1)) set("minutes")("1");
  };
  const validateSeconds = (raw) => {
    if (raw === "") return set("seconds")("00");
    const n = Math.min(59, Math.max(0, parseInt(raw, 10) || 0));
    set("seconds")(String(n).padStart(2, "0"));
  };

  /** Returns [title, text?] of the first problem, or null. */
  const validate = () => {
    if (form.rules.length === 0) return ["at least 1 rule required."];
    if (form.rules.some((r) => !r.trim())) return ["Please fill in all rule fields."];

    const count = Number(form.count);
    const negative = Number(form.negative);
    const positive = Number(form.positive);

    if (form.useTimeout && !(parseInt(form.minutes, 10) >= 1)) return ["Minutes must be at least 1"];
    if (form.negative === "" || isNaN(negative)) return ["Negative Dream count is required"];
    if (negative < 1 || negative > 20) return ["Negative Dream count must be between 1 and 20"];
    if (form.positive === "" || isNaN(positive)) return ["Positive Dream count is required"];
    if (positive < 1 || positive > 20) return ["Positive Dream count must be between 1 and 20"];
    if (form.count === "" || isNaN(count) || count < 1) return ["Total Dream count is required"];
    if (count > 20) return ["Total Dream count cannot be greater than 20"];
    if (negative + positive !== count) {
      return ["Total count mismatch", `Found ${negative + positive} items, but ${count} were expected`];
    }
    if (form.title.length > 200) return ["You exceed the maximum limit of title! "];
    if (form.thankYou.length > 300) return ["You exceed the maximum limit of thank you page! "];
    if (Number(form.points) < 0) return ["Invalid Correct Answer Points", "Correct answer points cannot be less than 0."];
    if (Number(form.points) > 100) return ["Invalid Correct Answer Points", "Correct answer points cannot be greater than 100."];
    if (Number(form.wrongPoints) < 0) return ["Invalid Wrong Answer Points", "Wrong answer points cannot be less than 0."];
    if (Number(form.wrongPoints) > 100) return ["Invalid Wrong Answer Points", "Wrong answer points cannot exceed 100."];
    if (form.rules.some((r) => r.length > 200)) return ["Rules must below 200 characters."];
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) return notifyError(problem[0], problem[1]);

    setSaving(true);
    try {
      await updateMultiple(themeName, {
        custom_text_thank_you_page: form.thankYou,
        landing_page_title: form.title,
        rules: form.rules.map((r) => r.trim()),
        landing_page_title_color: form.textColor,
        button_color: form.buttonColor,
        card_color_text: form.cardColor,
        icon_color: form.iconColor,
        use_timeout: form.useTimeout ? 1 : 0,
        shuffle: form.shuffle ? 1 : 0,
        timer: timerText,
        category_upload_count: Number(form.count),
        positive_upload_count: Number(form.positive),
        negtive_upload_count: Number(form.negative),
        points: form.points,
        wrong_points: form.wrongPoints,
      });
      await notifySuccess("Data Updated");
      navigate(`/admin/content/${themeName}`);
    } catch (err) {
      notifyError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dc-page">
      <BackButton to="/admin/themes" />

      <div className="dc-two-col">
        <div className="dc-card dc-card-tall">
          <h4 className="dc-card-title">Add Custom Title:</h4>
          <textarea className="dc-input" maxLength={200} value={form.title} onChange={(e) => set("title")(e.target.value)} />
          <p className="dc-note">
            <code>* Maximum character limit should be 200 characters.</code>
            <code>* This title will reflect on the main game page</code>
          </p>

          <h4 className="dc-card-title">Custom Text Thank You Page:</h4>
          <textarea className="dc-input" maxLength={300} value={form.thankYou} onChange={(e) => set("thankYou")(e.target.value)} />
          <p className="dc-note">
            <code>* This text will reflect on the thank you page. </code>
            <code>* Maximum character limit should be 300 characters.</code>
          </p>

          <h4 className="dc-card-title">Add Custom Rules</h4>
          {form.rules.map((rule, i) => (
            <div className="dc-rule" key={i}>
              <textarea className="dc-input" placeholder="Rules" value={rule} onChange={(e) => setRule(i, e.target.value)} />
              <button
                type="button"
                className="dc-btn dc-btn-danger"
                title="Remove rule"
                onClick={() => set("rules")(form.rules.filter((_, idx) => idx !== i))}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          ))}
          {form.rules.length < MAX_RULES && (
            <button type="button" className="dc-btn dc-btn-primary" onClick={() => set("rules")([...form.rules, ""])}>
              <FontAwesomeIcon icon={faPlus} /> Add new rule
            </button>
          )}
          <p className="dc-note">
            <code>* Keep 6 rules at the most</code>
            <code>* At least 1 rule is required</code>
            <code>* Character limit of each rule is 200</code>
          </p>
        </div>

        <div className="dc-card dc-card-tall">
          <h4 className="dc-card-title">Page Content</h4>
          <NumberRow label="Correct Answer Points" min="1" max="100" value={form.points} onChange={set("points")} />
          <NumberRow label="Wrong Answer Points" min="0" max="100" value={form.wrongPoints} onChange={set("wrongPoints")} />
          <NumberRow label="Dream Show count" numericOnly min="2" max="20" value={form.count} onChange={set("count")} />
          <NumberRow label="Positive Dream Show count" numericOnly min="1" max="20" value={form.positive} onChange={set("positive")} />
          <NumberRow label="Negative Dream Show count" numericOnly min="1" max="20" value={form.negative} onChange={set("negative")} />

          <div className="dc-row">
            <Switch id="shuffle" checked={form.shuffle} onChange={set("shuffle")} label="shuffle words" />
          </div>

          <ColorRow label="Text Color" value={form.textColor} onChange={set("textColor")} />
          <ColorRow label="Button Box Color" value={form.buttonColor} onChange={set("buttonColor")} />
          <ColorRow label="Card text color" value={form.cardColor} onChange={set("cardColor")} />
          <ColorRow label="Icon Color" value={form.iconColor} onChange={set("iconColor")} />

          <div className="dc-center">
            <Switch id="timeout" checked={form.useTimeout} onChange={set("useTimeout")} label="Enable Timeout" />
            {form.useTimeout && (
              <div className="dc-timer">
                <h4 className="dc-card-title">Timer:</h4>
                <label>Minutes:</label>
                <input
                  type="number"
                  className="dc-input dc-timer-input"
                  min="1"
                  value={form.minutes}
                  onChange={(e) => set("minutes")(e.target.value)}
                  onBlur={validateMinutes}
                />
                <label>Seconds:</label>
                <input
                  type="number"
                  className="dc-input dc-timer-input"
                  min="0"
                  max="59"
                  value={form.seconds}
                  onChange={(e) => set("seconds")(e.target.value)}
                  onBlur={(e) => validateSeconds(e.target.value)}
                />
                <p className="dc-timer-display">{timerText}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="dc-center">
        <button className="dc-btn dc-btn-success" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Next"} <FontAwesomeIcon icon={faAnglesRight} />
        </button>
      </div>
    </div>
  );
}
